import React from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import { RESOURCE_TYPES } from '../../constants.js';

import SelectedResourceRow from './SelectedResourceRow.jsx';

/**
 * Tarjeta compacta de una unidad de aprendizaje: muestra de un vistazo todo lo
 * que la unidad lleva seleccionado, en tres columnas por tipo de recurso.
 *
 * Replica el layout de LearningPathView a propósito, para que armar la ruta se
 * vea igual que consultarla. No trae el catálogo: explorar y elegir vive en
 * ResourceSelectionModal, que se abre desde el "Agregar" de cada columna.
 *
 * Props:
 * - unit: { learnunit_id, name }.
 * - index: posición de la unidad en el curso (solo para el número que se muestra).
 * - selectionsByType: { [resource_type]: [{ key, path, name, thumbnail }] }.
 * - onAdd(typeKey): abre el catálogo para esta unidad y ese tipo.
 * - onRemove(typeKey, key): quita un recurso ya seleccionado.
 */
const UnitResourcesCard = ({ unit, index, selectionsByType = {}, onAdd, onRemove }) => {
  const total = RESOURCE_TYPES.reduce(
    (acc, type) => acc + (selectionsByType[type.key]?.length || 0),
    0
  );

  return (
    <div className='rounded-xl border border-border bg-white'>
      <div className='flex items-center justify-between gap-4 px-6 py-5'>
        <div className='flex items-center gap-4'>
          <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-lg font-bold text-white'>
            {index + 1}
          </span>
          <div className='flex flex-col text-left'>
            <span className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              Unidad {index + 1}
            </span>
            <span className='text-lg font-bold text-primary'>{unit.name}</span>
          </div>
        </div>
        <span className='shrink-0 rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground'>
          {total} {total === 1 ? 'recurso' : 'recursos'}
        </span>
      </div>

      <div className='grid gap-6 border-t border-border px-6 py-6 md:grid-cols-3'>
        {RESOURCE_TYPES.map(type => {
          const { Icon } = type;
          const entries = selectionsByType[type.key] || [];
          return (
            <div key={type.key} className='flex flex-col'>
              <div className={`mb-3 flex items-center gap-2 ${type.header}`}>
                <Icon fontSize='small' />
                <span className='text-sm font-semibold uppercase tracking-wider'>{type.label}</span>
                <span
                  className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${type.badge}`}>
                  {entries.length}
                </span>
              </div>

              {/* flex-1 empuja el "Agregar" al pie, así queda alineado entre columnas */}
              <div className='flex flex-1 flex-col gap-2'>
                {entries.length === 0 ? (
                  <p className='text-left text-sm text-muted-foreground'>Sin recursos.</p>
                ) : (
                  entries.map(entry => (
                    <SelectedResourceRow
                      key={(entry.path || [entry.key]).join('/')}
                      type={type}
                      entry={entry}
                      onRemove={() => onRemove(type.key, entry.key)}
                    />
                  ))
                )}
              </div>

              <button
                type='button'
                onClick={() => onAdd(type.key)}
                className='mt-3 inline-flex items-center justify-center gap-1 rounded-full border border-dashed border-border px-4 py-2 text-sm font-semibold text-primary transition duration-150 hover:border-secondary hover:bg-background'>
                <AddOutlinedIcon fontSize='small' />
                Agregar
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnitResourcesCard;
